import React, { useEffect, useMemo, useState } from "react";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";

// ---------- helpers ----------
function toDisplay(v) {
  if (v === null || v === undefined) return "-";
  try {
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v);
      if (!isNaN(d)) return d.toLocaleString("en-GB");
    }
    if (v && typeof v === "object" && v.$date) {
      const d = new Date(v.$date);
      if (!isNaN(d)) return d.toLocaleString("en-GB");
    }
  } catch {}
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

function getValidDate(v) {
  try {
    if (!v) return null;
    if (typeof v === "string") {
      const d = new Date(v);
      return isNaN(d) ? null : d;
    }
    if (v && typeof v === "object" && v.$date) {
      const d = new Date(v.$date);
      return isNaN(d) ? null : d;
    }
  } catch {}
  return null;
}

function bestRowDate(row) {
  const prefs = [
    "createdAt",
    "deviceReceivedAt",
    "androidServiceInvokedAt",
    "sentByBackendAt",
    "smsSentReceiptAt",
    "smsDeliveryReceiptAt",
  ];
  for (const k of prefs) {
    const d = getValidDate(row[k]);
    if (d) return d;
  }
  return null;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function truncate(str, n) {
  if (str == null) return "-";
  const s = String(str);
  if (s.length <= n) return s;
  return s.slice(0, n) + "...";
}

// show only last 4 digits of a phone number
function lastFiveDigits(value) {
  if (value == null) return "-";
  const digitsOnly = String(value).replace(/\D/g, "");
  if (!digitsOnly) return "-";
  return digitsOnly.slice(-4) || "-";
}

// =====================================================

export default function SMSQueue() {
  const masked = useMaskNames();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState([]); // kept but not used directly
  const [search, setSearch] = useState("");

  // default to SUMMARY (today)
  const [detail, setDetail] = useState(false);
  const [summaryChecked, setSummaryChecked] = useState(true);
  const [summaryDay, setSummaryDay] = useState("today"); // 'today' | 'yesterday'

  // filters (defaults set to false as requested)
  const [hideDelivered, setHideDelivered] = useState(false);
  const [hideDeleted, setHideDeleted] = useState(false);

  // sorting (detail)
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  // sorting (summary)
  const [summarySortBy, setSummarySortBy] = useState("pharmacyName");
  const [summarySortDir, setSummarySortDir] = useState("asc");

  // ---------- initial load (no auto-refresh) ----------
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const authToken = localStorage.getItem("authToken") || localStorage.getItem("token");
        const r = await fetch("https://api.pharmacegroup.co.uk/sms-queue", {
          method: "GET",
          headers: { ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        });
        const raw = await r.text();
        if (!r.ok) {
          console.error("Failed to load /sms-queue", r.status, raw);
          setEntries([]);
          setSummary([]);
          return;
        }
        const j = raw ? JSON.parse(raw) : {};

        // ---- Client-side limit to the most recent 5,000 entries ----
        const rawEntries = Array.isArray(j.entries) ? j.entries : [];
        const sortedByBestDateDesc = rawEntries
          .slice()
          .sort((a, b) => {
            const da = bestRowDate(a);
            const db = bestRowDate(b);
            if (da && db) return db - da; // newer first
            if (da && !db) return -1;
            if (!da && db) return 1;
            return 0;
          });
        const limited = sortedByBestDateDesc.slice(0, 5000);

        setEntries(limited);
        setSummary(Array.isArray(j.summary) ? j.summary : []);
      } catch (e) {
        console.error("Failed to load /sms-queue", e);
        setEntries([]);
        setSummary([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---------- table columns (detail) ----------
  const preferredOrder = [
    "pharmacyName",
    "contactNumber",
    "message",
    "status",
    "deviceReceivedAt",
    "androidServiceInvokedAt",
    "createdAt",
    "sentByBackendAt",
    "smsSentReceiptAt",
    "smsDeliveryReceiptAt",
    "simDeliveryFailReason",
    "processOutcome",
  ];

  const headerLabels = {
    pharmacyName: "Pharmacy",
    contactNumber: "contactNumber",
    message: "message",
    status: "status",
    deviceReceivedAt: "Received",
    androidServiceInvokedAt: "Invoked",
    createdAt: "Send requested",
    sentByBackendAt: "Backend send",
    smsSentReceiptAt: "Send receipt",
    smsDeliveryReceiptAt: "Delivery receipt",
    simDeliveryFailReason: "sim message",
    processOutcome: "processOutcome",
  };

  const allKeys = useMemo(() => {
    const base = new Set();
    for (const row of entries) Object.keys(row).forEach(k => base.add(k));
    ["_id","dbName","deletedAt","updatedAt","messageId","pharmacyId","ph"].forEach(k => base.delete(k));
    const present = Array.from(base);
    const ordered = [];
    preferredOrder.forEach(k => { if (present.includes(k)) ordered.push(k); });
    present.forEach(k => { if (!ordered.includes(k)) ordered.push(k); });
    return ordered;
  }, [entries]);

  // ---------- filter + search for DETAIL ----------
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return entries.filter(row => {
      if (hideDeleted && row.isDeleted === true) return false;
      if (hideDelivered && String(row.status).toUpperCase() === "DELIVERED") return false;

      if (!term) return true;
      return JSON.stringify(row).toLowerCase().includes(term);
    });
  }, [entries, search, hideDelivered, hideDeleted]);

  // ---------- sorting (detail) ----------
  const sorted = useMemo(() => {
    const data = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    data.sort((a, b) => {
      const va = a[sortBy];
      const vb = b[sortBy];
      const da = getValidDate(va);
      const db = getValidDate(vb);
      if (da && db) return (da - db) * dir;
      const sa = toDisplay(va).toLowerCase();
      const sb = toDisplay(vb).toLowerCase();
      if (sa < sb) return -1 * dir;
      if (sa > sb) return  1 * dir;
      return 0;
    });
    return data;
  }, [filtered, sortBy, sortDir]);

  function onHeaderClick(k) {
    if (sortBy === k) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(k); setSortDir("asc"); }
  }

  // ---------- SUMMARY (today or yesterday) ----------
  const today = new Date();
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  const targetDay = summaryDay === "yesterday" ? yesterday : today;

  const filteredForSummary = useMemo(() => {
    // summary is count-based; just apply Hide delivered/Hide deleted and the day selection
    return entries.filter(row => {
      if (hideDeleted && row.isDeleted === true) return false;
      if (hideDelivered && String(row.status).toUpperCase() === "DELIVERED") return false;
      const d = bestRowDate(row);
      return !!d && isSameDay(d, targetDay);
    });
  }, [entries, hideDeleted, hideDelivered, targetDay]);

  const summaryRows = useMemo(() => {
    // name -> { total, sentViaSim, backupSend, notDelivered }
    const byPharmacy = new Map();

    for (const row of filteredForSummary) {
      const name = row.pharmacyName || "-";
      const status = String(row.status || "UNKNOWN").toUpperCase();
      const hasBackendSend = !!getValidDate(row.sentByBackendAt);

      if (!byPharmacy.has(name)) {
        byPharmacy.set(name, { total: 0, sentViaSim: 0, backupSend: 0, notDelivered: 0 });
      }
      const agg = byPharmacy.get(name);
      agg.total += 1;

      if (status === "DELIVERED") {
        if (hasBackendSend) agg.backupSend += 1;
        else agg.sentViaSim += 1; // delivered but no backend send timestamp
      } else {
        agg.notDelivered += 1;
      }
    }

    const arr = [];
    for (const [pharmacyName, vals] of byPharmacy.entries()) {
      arr.push({ pharmacyName, ...vals });
    }

    // apply summary sorting
    const dir = summarySortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const k = summarySortBy;
      if (k === "pharmacyName") {
        const sa = (a.pharmacyName || "").toString().toLowerCase();
        const sb = (b.pharmacyName || "").toString().toLowerCase();
        if (sa < sb) return -1 * dir;
        if (sa > sb) return  1 * dir;
        return 0;
      }
      const na = Number(a[k] ?? 0);
      const nb = Number(b[k] ?? 0);
      if (na < nb) return -1 * dir;
      if (na > nb) return  1 * dir;
      return 0;
    });

    return arr;
  }, [filteredForSummary, summarySortBy, summarySortDir]);

  function onSummaryHeaderClick(k) {
    if (summarySortBy === k) {
      setSummarySortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSummarySortBy(k);
      setSummarySortDir("asc");
    }
  }

  return (
    <div className="p-4 space-y-3">
      <style>{`
        .smsq-table {
          width: 100%;
          table-layout: fixed;
          border: 1px solid #d1d5db;
          border-collapse: separate;
          border-spacing: 0;
        }
        .smsq-table thead th {
          background: #f9fafb;
          font-weight: 600;
        }
        .smsq-table th,
        .smsq-table td {
          border-right: 1px solid #e5e7eb;
          padding: 0.5rem 1rem;
          vertical-align: top;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .smsq-table th:last-child,
        .smsq-table td:last-child {
          border-right: none;
        }
        .smsq-table tbody tr:nth-child(even) {
          background-color: #f5f5f5;
        }
        .smsq-table thead th,
        .smsq-table tbody td {
          border-bottom: 1px solid #e5e7eb;
        }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 className="text-xl font-semibold">SMS queue</h1>
        <MaskToggleButton />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search anything..."
          className="border rounded px-3 py-2"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {/* Detail / Summary toggles (default: Summary) */}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={detail}
            onChange={(e) => { setDetail(e.target.checked); setSummaryChecked(!e.target.checked); }}
          />
          Detail
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={summaryChecked}
            onChange={(e) => { setSummaryChecked(e.target.checked); setDetail(!e.target.checked); }}
          />
          Summary
        </label>

        {/* Filters */}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={hideDelivered}
            onChange={(e) => setHideDelivered(e.target.checked)}
          />
          Hide delivered
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={hideDeleted}
            onChange={(e) => setHideDeleted(e.target.checked)}
          />
          Hide deleted
        </label>

        {/* Summary day selector (today / yesterday) */}
        {summaryChecked && (
          <div className="flex items-center gap-3 ml-2">
            <span className="text-sm font-medium">Day:</span>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="summary-day"
                value="today"
                checked={summaryDay === "today"}
                onChange={() => setSummaryDay("today")}
              />
              Today
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="summary-day"
                value="yesterday"
                checked={summaryDay === "yesterday"}
                onChange={() => setSummaryDay("yesterday")}
              />
              Yesterday
            </label>
          </div>
        )}
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : detail ? (
        <div className="overflow-auto">
          <table className="smsq-table">
            <thead>
              <tr>
                {allKeys.map(k => (
                  <th
                    key={k}
                    onClick={() => onHeaderClick(k)}
                    className="text-left cursor-pointer select-none"
                    title="Click to sort"
                  >
                    {headerLabels[k] || k}{sortBy === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, idx) => (
                <tr key={idx}>
                  {allKeys.map(k => {
                    if (k === "message") {
                      const full = row[k] ?? "";
                      return (
                        <td key={k} title={String(full)}>
                          {truncate(full, 20)}
                        </td>
                      );
                    }
                    if (k === "contactNumber") {
                      const fullContact = row[k] ?? "";
                      return (
                        <td key={k} title={String(fullContact)}>
                          {lastFiveDigits(fullContact)}
                        </td>
                      );
                    }
                    return <td key={k}>{toDisplay(row[k])}</td>;
                  })}
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td className="px-3 py-4 text-gray-500" colSpan={allKeys.length}>No matching entries.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        // SUMMARY VIEW (today or yesterday) with sortable columns
        <div className="overflow-auto">
          <table className="smsq-table">
            <thead>
              <tr>
                <th
                  className="text-left cursor-pointer select-none"
                  onClick={() => onSummaryHeaderClick("pharmacyName")}
                  title="Click to sort"
                >
                  Pharmacy{summarySortBy === "pharmacyName" ? (summarySortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
                <th
                  className="text-left cursor-pointer select-none"
                  onClick={() => onSummaryHeaderClick("total")}
                  title="Click to sort"
                >
                  Total{summarySortBy === "total" ? (summarySortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
                <th
                  className="text-left cursor-pointer select-none"
                  onClick={() => onSummaryHeaderClick("sentViaSim")}
                  title="Click to sort"
                >
                  Sent via sim{summarySortBy === "sentViaSim" ? (summarySortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
                <th
                  className="text-left cursor-pointer select-none"
                  onClick={() => onSummaryHeaderClick("backupSend")}
                  title="Click to sort"
                >
                  Backup send{summarySortBy === "backupSend" ? (summarySortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
                <th
                  className="text-left cursor-pointer select-none"
                  onClick={() => onSummaryHeaderClick("notDelivered")}
                  title="Click to sort"
                >
                  Not delivered{summarySortBy === "notDelivered" ? (summarySortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((r, i) => (
                <tr key={i}>
                  <td>{displayName(r.pharmacyName, masked)}</td>
                  <td>{r.total}</td>
                  <td>{r.sentViaSim}</td>
                  <td>{r.backupSend}</td>
                  <td>{r.notDelivered}</td>
                </tr>
              ))}
              {summaryRows.length === 0 && (
                <tr><td className="px-3 py-4 text-gray-500" colSpan={5}>No summary ({summaryDay}).</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
